import { useEffect, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import { copyToClipboard } from '@/lib/share';

const values = [
  {
    icon: 'ri-book-open-line',
    title: '内容为先',
    desc: '以影片事实与文本分析为基础，拒绝标题党，用专业视角呈现每一部作品的来龙去脉。',
  },
  {
    icon: 'ri-lightbulb-line',
    title: '深度解读',
    desc: '不止于剧情简介，更关注影像语言、作者风格与社会语境，帮助影迷建立系统认知。',
  },
  {
    icon: 'ri-layout-masonry-line',
    title: '编辑美学',
    desc: '借鉴杂志编辑的版面思维，用清晰的层级与克制的视觉，营造舒适的深度阅读体验。',
  },
];

const team = [
  { name: '沈砚', role: '主编 / 影评人', desc: '关注东亚作者电影与类型叙事，长期撰写韩国电影专题。', avatar: 'https://readdy.ai/api/search-image?query=editorial%20portrait%20of%20a%20thoughtful%20asian%20man%20in%20muted%20olive%20shirt%2C%20clean%20ivory%20background%2C%20soft%20studio%20light%2C%20minimalist%20photography&width=400&height=400&seq=kfilm-team-01&orientation=squarish' },
  { name: '林知远', role: '资深撰稿 / 研究者', desc: '电影学背景，研究韩国电影工业与跨文化传播。', avatar: 'https://readdy.ai/api/search-image?query=editorial%20portrait%20of%20an%20asian%20woman%20with%20short%20hair%20in%20beige%20knitwear%2C%20clean%20ivory%20background%2C%20soft%20studio%20light%2C%20minimalist%20photography&width=400&height=400&seq=kfilm-team-02&orientation=squarish' },
  { name: '苏晚', role: '特约影评人', desc: '专注女性视角与情感叙事，影展前线观察者。', avatar: 'https://readdy.ai/api/search-image?query=editorial%20portrait%20of%20an%20asian%20woman%20with%20long%20hair%20in%20a%20light%20shirt%2C%20clean%20ivory%20background%2C%20soft%20studio%20light%2C%20minimalist%20photography&width=400&height=400&seq=kfilm-team-03&orientation=squarish' },
  { name: '程野', role: '产业观察', desc: '追踪流媒体与电影产业动态，输出行业分析。', avatar: 'https://readdy.ai/api/search-image?query=editorial%20portrait%20of%20an%20asian%20man%20with%20glasses%20in%20a%20navy%20shirt%2C%20clean%20ivory%20background%2C%20soft%20studio%20light%2C%20minimalist%20photography&width=400&height=400&seq=kfilm-team-04&orientation=squarish' },
];

export default function About() {
  const [toast, setToast] = useState('');
  const email = 'hello@hanguolilunpian.com';

  useEffect(() => {
    document.title = '关于我们 - 韩国理论片影视内容网站';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        'content',
        '了解「韩国理论片」网站定位、编辑团队与联系方式。我们专注韩国剧情与理论电影的介绍、影评解析与资讯整理。',
      );
    }
  }, []);

  const handleCopyEmail = async () => {
    const ok = await copyToClipboard(email);
    setToast(ok ? '邮箱已复制到剪贴板' : '复制失败，请手动复制');
    window.setTimeout(() => setToast(''), 2200);
  };

  return (
    <SiteLayout>
      <Breadcrumb items={[{ label: '关于我们' }]} />

      {/* 定位 */}
      <section className="rounded-lg overflow-hidden border border-background-200/80 bg-background-50">
        <div className="relative w-full h-[220px] md:h-[300px] bg-primary-950">
          <img
            src="https://readdy.ai/api/search-image?query=cinematic%20editorial%20banner%20of%20a%20dark%20navy%20screening%20room%20with%20rows%20of%20seats%20and%20warm%20amber%20projector%20light%2C%20moody%20atmospheric%2C%20minimalist%2C%20no%20text&width=1600&height=600&seq=kfilm-about-hero&orientation=landscape"
            alt="韩国理论片网站视觉主图"
            title="韩国理论片 · 关于我们"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/50 to-primary-950/30" />
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
            <p className="font-label text-xs font-semibold uppercase tracking-[0.22em] text-accent-400">
              About Us
            </p>
            <h1 className="mt-2 font-heading text-2xl md:text-4xl font-extrabold text-background-50">
              关于「韩国理论片」
            </h1>
          </div>
        </div>
        <div className="p-6 md:p-10">
          <p className="text-sm md:text-base leading-loose text-foreground-700">
            「韩国理论片」是一个专注于韩国剧情电影及理论电影的影视内容网站。我们收集并整理最新的韩国剧情类影片、影史经典理论片，提供影片介绍、深度影评解析与相关行业资讯，
            帮助喜欢深度解读韩国电影艺术的用户，在浏览与阅读中获取更丰富的知识与观影推荐。
          </p>
          <p className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
            我们把网站定位为一份「可阅读的电影杂志」：以编辑的版面思维组织信息，让知识型阅读既专业又有节奏感，适合电影爱好者、影评人、影视研究者与普通观众。
          </p>
        </div>
      </section>

      {/* 价值观 */}
      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground-950">我们的坚持</h2>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {values.map((v) => (
            <div key={v.title} className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className={`${v.icon} text-2xl`} aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 团队 */}
      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground-950">编辑团队</h2>
        <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {team.map((t) => (
            <div key={t.name} className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden">
              <div className="w-full aspect-square bg-background-200">
                <img src={t.avatar} alt={`${t.name} 头像`} title={`${t.name} · ${t.role}`} className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-4">
                <h3 className="font-heading text-base font-bold text-foreground-950">{t.name}</h3>
                <p className="mt-0.5 font-label text-xs text-accent-700">{t.role}</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 联系方式 */}
      <section className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lg:col-span-2 rounded-lg border border-background-200/80 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-contacts-line text-primary-500" aria-hidden="true" />
            联系我们
          </h2>
          <p className="mt-2 text-sm text-foreground-600">欢迎投稿、约稿、内容合作或纠错反馈。</p>

          <div className="mt-5 flex items-center gap-3 rounded-md border border-background-200 bg-background-100 p-3">
            <i className="ri-mail-line text-xl text-accent-600" aria-hidden="true" />
            <a href={`mailto:${email}`} className="flex-1 text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer">
              {email}
            </a>
            <button
              type="button"
              onClick={handleCopyEmail}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-primary-500 font-label text-xs font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-copy-line" aria-hidden="true" />
              复制邮箱
            </button>
          </div>

          <ul className="mt-5 space-y-3 text-sm text-foreground-700">
            <li className="flex items-start gap-2">
              <i className="ri-wechat-line mt-0.5 text-primary-400" aria-hidden="true" />
              <span>微信公众号：韩国理论片（ID：koreanfilmtheory）</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-map-pin-line mt-0.5 text-primary-400" aria-hidden="true" />
              <span>内容编辑部：首尔 / 上海</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-time-line mt-0.5 text-primary-400" aria-hidden="true" />
              <span>工作时间：周一至周五 10:00 - 19:00</span>
            </li>
          </ul>
        </div>

        <div className="rounded-lg border border-background-200/80 bg-background-50 p-6 flex flex-col items-center text-center">
          <h3 className="font-heading text-base font-bold text-foreground-950">扫码关注公众号</h3>
          <div className="mt-4 w-40 h-40 rounded-md overflow-hidden bg-background-100 border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=clean%20minimal%20black%20and%20white%20qr%20code%20graphic%20centered%20on%20white%20background%2C%20crisp%20square%20modules%2C%20flat%20design%2C%20no%20text&width=400&height=400&seq=kfilm-wechat-qr&orientation=squarish"
              alt="微信公众号二维码"
              title="韩国理论片 微信公众号"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <p className="mt-3 text-xs text-foreground-500">扫描二维码，获取每周影评与专题推送</p>
          <p className="mt-1 text-[11px] text-foreground-400">（示意图，非真实可扫码二维码）</p>
        </div>
      </section>

      {/* 版权声明 */}
      <section className="mt-12 rounded-lg border border-background-200/80 bg-background-100/60 p-6 md:p-8">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
          <i className="ri-copyright-line text-primary-500" aria-hidden="true" />
          版权声明与使用须知
        </h2>
        <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
          <li className="flex items-start gap-2">
            <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-500" aria-hidden="true" />
            本站收录的影片介绍、影评与资讯内容，均为基于公开资料的编辑整理或虚构示范内容，用于产品功能演示，不构成任何商业用途。
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-500" aria-hidden="true" />
            站内搜索、分享与收藏均为前端演示功能：搜索为本地匹配，分享为模拟跳转，收藏保存在你的浏览器本地，不会上传服务器。
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-500" aria-hidden="true" />
            未经许可，请勿将本站内容用于商业转载；如需引用，请注明来源与原文链接。
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-500" aria-hidden="true" />
            如对站内内容有任何异议或建议，欢迎通过上方邮箱与我们联系。
          </li>
        </ul>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-up">
          <div className="flex items-center gap-2 rounded-full bg-foreground-950 px-4 py-2.5 text-sm text-background-50">
            <i className="ri-checkbox-circle-fill text-accent-400" aria-hidden="true" />
            {toast}
          </div>
        </div>
      )}
    </SiteLayout>
  );
}